import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, ChevronDown, Globe, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

export const Navbar = () => {
  const { t, language, setLanguage, languages } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/courses', label: t('nav.courses') },
    { href: '/news', label: t('nav.news') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;
  const currentLanguage = languages.find(l => l.code === language);

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_jwsea-edu/artifacts/885p674v_image.png" 
              alt="JWSEA Logo" 
              className="h-8 md:h-12 w-auto"
            />
            <div>
              <span className="font-bold text-gray-900" style={{ fontSize: 'clamp(0.7rem, 2.5vw, 1rem)' }}>JWSEA</span>
              <span className="block text-gray-500" style={{ fontSize: 'clamp(0.55rem, 2vw, 0.75rem)' }}>Foundation</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-[#00A651] bg-[#e6f6ec]'
                    : 'text-gray-700 hover:text-[#00A651] hover:bg-gray-50'
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 px-3 py-2 h-9 text-sm border-gray-200"
                  data-testid="language-switcher"
                  aria-label={t('common.selectLanguage')}
                >
                  <img 
                    src={`https://flagcdn.com/w40/${currentLanguage?.countryCode}.png`}
                    alt={currentLanguage?.name}
                    className="h-4 w-6 object-cover rounded-sm"
                  />
                  <span className="hidden sm:inline">{currentLanguage?.name}</span>
                  <span className="sm:hidden">{currentLanguage?.code.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-[#e6f6ec] text-[#00A651]' : ''}`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <img 
                      src={`https://flagcdn.com/w40/${lang.countryCode}.png`}
                      alt={lang.name}
                      className="h-4 w-6 object-cover rounded-sm mr-2"
                    />
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Apply Button */}
            <Link to="/apply" className="hidden md:block">
              <Button 
                className="bg-[#00A651] hover:bg-[#008a43] text-white font-bold px-6"
                data-testid="apply-button"
              >
                {t('nav.apply')}
              </Button>
            </Link>

            {/* User Menu / Login */}
            {isAuthenticated ? (
              <Link to="/mypage" className="hidden md:block">
                <Button 
                  variant="outline"
                  className="flex items-center gap-2 border-[#00A651] text-[#00A651] hover:bg-[#e6f6ec]"
                  data-testid="mypage-button"
                >
                  <User className="h-4 w-4" />
                  <span className="max-w-[120px] truncate">{user?.name}</span>
                  <span className="text-gray-400">|</span>
                  <span>{t('navbar.mypage')}</span>
                </Button>
              </Link>
            ) : (
              <Link to="/register" className="hidden md:block">
                <Button 
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-5"
                  data-testid="login-button"
                >
                  <User className="h-4 w-4" />
                  {t('navbar.loginRegister')}
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-3 rounded-md text-base font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-[#00A651] bg-[#e6f6ec]'
                      : 'text-gray-700 hover:text-[#00A651] hover:bg-gray-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/apply"
                className="mt-2 mx-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button 
                  className="w-full bg-[#00A651] hover:bg-[#008a43] text-white font-bold"
                  data-testid="mobile-apply-button"
                >
                  {t('nav.apply')}
                </Button>
              </Link>
              {isAuthenticated ? (
                <Link
                  to="/mypage"
                  className="mt-2 mx-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button 
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 border-[#00A651] text-[#00A651]"
                    data-testid="mobile-mypage-button"
                  >
                    <User className="h-4 w-4" />
                    {t('navbar.mypage')}
                  </Button>
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="mt-2 mx-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button 
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                    data-testid="mobile-login-button"
                  >
                    <User className="h-4 w-4" />
                    {t('navbar.loginRegisterMobile')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
